// @ts-nocheck
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/client/react'

import { CrossIcon } from '@pleio/icons/cross'

import Button from 'js/components/Button'
import Flexer from 'js/components/Flexer/Flexer'
import IconButton from 'js/components/IconButton'
import ConfirmationPopover from 'js/components/Popover/ConfirmationPopover'
import Text from 'js/components/Text/Text'
import UserLink from 'js/components/UserLink'

import type { InvitedListQuery } from './Invited.queries.generated'
import { DELETE_INVITED, RESEND_INVITE } from './InvitedItem.mutations'

type InvitedEvent = Extract<InvitedListQuery['entity'], { __typename: 'Event' }>

type Props = {
    eventGuid: string
    invited: NonNullable<
        NonNullable<NonNullable<InvitedEvent['attendees']>['edges']>[number]
    >
}

const InvitedItem = ({ eventGuid, invited }: Props) => {
    const { t } = useTranslation()

    const [inviteSent, setInviteSent] = useState(false)
    const [showResendInvite, setShowResendInvite] = useState(false)
    const [resendInvite, { loading: resendLoading, error: resendError }] =
        useMutation(RESEND_INVITE)
    const handleResendInvite = async () => {
        await resendInvite({
            variables: {
                input: {
                    guid: eventGuid,
                    users: [{ email }],
                    resend: true,
                },
            },
        })
            .then(() => {
                setShowResendInvite(false)
                setInviteSent(true)
            })
            .catch((errors) => {
                console.error(errors)
            })
    }

    const [showDeleteInvited, setShowDeleteInvited] = useState(false)
    const [deleteInvited, { loading: deleteLoading, error: deleteError }] =
        useMutation(DELETE_INVITED)
    const handleDeleteInvited = async () => {
        await deleteInvited({
            variables: {
                input: {
                    guid: eventGuid,
                    emailAddresses: [email],
                },
            },
            refetchQueries: ['InvitedList'],
        })
            .then(() => {
                setShowDeleteInvited(false)
            })
            .catch((errors) => {
                console.error(errors)
            })
    }

    const { guid, email } = invited

    return (
        <Flexer
            key={`invited-${guid || email}`}
            style={{
                alignItems: 'center',
                justifyContent: 'space-between',
            }}
        >
            <UserLink
                entity={invited}
                showEmail
                style={{
                    width: '100%',
                    flex: 1,
                }}
            />
            <Flexer gutterSize="sm">
                {inviteSent ? (
                    <Text size="small">{t('global.invitation-sent')}</Text>
                ) : (
                    <div>
                        <ConfirmationPopover
                            visible={showResendInvite}
                            setVisible={setShowResendInvite}
                            title={t('entity-event.resend-invite')}
                            description={t('global.are-you-sure')}
                            onConfirm={handleResendInvite}
                            loading={resendLoading}
                            error={resendError}
                        >
                            <Button
                                size="normal"
                                variant="secondary"
                                aria-label={t('entity-event.resend-invite')}
                                onClick={() =>
                                    setShowResendInvite(!showResendInvite)
                                }
                            >
                                {t('action.resend')}
                            </Button>
                        </ConfirmationPopover>
                    </div>
                )}

                <div>
                    <ConfirmationPopover
                        visible={showDeleteInvited}
                        setVisible={setShowDeleteInvited}
                        title={t('entity-event.remove-invited')}
                        description={t('global.are-you-sure')}
                        onConfirm={handleDeleteInvited}
                        loading={deleteLoading}
                        error={deleteError}
                    >
                        <IconButton
                            size="large"
                            bgOffset="sm"
                            variant="secondary"
                            aria-label={t('entity-event.remove-invited')}
                            tooltip={t('action.remove')}
                            onClick={() =>
                                setShowDeleteInvited(!showDeleteInvited)
                            }
                        >
                            <CrossIcon />
                        </IconButton>
                    </ConfirmationPopover>
                </div>
            </Flexer>
        </Flexer>
    )
}

export default InvitedItem
